import React, { useState, useEffect, useRef } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw, convertFromHTML } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { ExternalLink, FileText } from 'lucide-react';

interface TermsEditorProps {
  termsType?: 'external' | 'internal' | null;
  termsContent?: string;
  onTermsTypeChange: (type: 'external' | 'internal' | null) => void;
  onTermsContentChange: (content: string) => void;
}

const TermsEditor: React.FC<TermsEditorProps> = ({
  termsType,
  termsContent,
  onTermsTypeChange,
  onTermsContentChange
}) => {
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const initializedRef = useRef(false);
  const prevTermsTypeRef = useRef(termsType);

  // Inicializar editor com conteúdo existente (apenas na primeira vez ou ao trocar de tipo)
  useEffect(() => {
    const typeChanged = prevTermsTypeRef.current !== termsType;
    prevTermsTypeRef.current = termsType;

    if (termsType === 'internal') {
      // Só inicializar se o tipo mudou para internal OU se é a primeira renderização com conteúdo
      if ((typeChanged || !initializedRef.current) && termsContent) {
        try {
          const blocksFromHTML = convertFromHTML(termsContent);
          const contentState = ContentState.createFromBlockArray(
            blocksFromHTML.contentBlocks,
            blocksFromHTML.entityMap
          );
          setEditorState(EditorState.createWithContent(contentState));
          initializedRef.current = true;
        } catch (error) {
          console.error('Erro ao carregar conteúdo:', error);
        }
      } else if (typeChanged && !termsContent) {
        setEditorState(EditorState.createEmpty());
        initializedRef.current = true;
      }
    } else {
      // Limpar o editor quando mudar para externo ou nenhum
      setEditorState(EditorState.createEmpty());
      initializedRef.current = false;
    }
  }, [termsType, termsContent]);

  const handleEditorChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
    onTermsContentChange(html);
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-tinta">
          Regulamento do Sorteio
        </label>
        <p className="text-xs text-tinta-soft italic mt-1 mb-3">
          Link externo ou editor de texto (opcional)
        </p>

        <div className="grid grid-cols-3 gap-2 mb-3">
          <button
            type="button"
            onClick={() => {
              onTermsTypeChange(null);
              setEditorState(EditorState.createEmpty());
            }}
            className={`px-2 py-2.5 border rounded-lg transition-colors text-xs sm:text-sm font-bold ${
              !termsType
                ? 'border-bordo bg-bordo/10 text-bordo-dark'
                : 'border-tinta/15 bg-white text-tinta-soft hover:bg-creme-light hover:text-tinta'
            }`}
          >
            <div className="flex flex-col items-center justify-center space-y-1">
              <span>Nenhum</span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => {
              onTermsTypeChange('external');
              if (termsType !== 'external') {
                onTermsContentChange('');
                setEditorState(EditorState.createEmpty());
              }
            }}
            className={`px-2 py-2.5 border rounded-lg transition-colors text-xs sm:text-sm font-bold ${
              termsType === 'external'
                ? 'border-bordo bg-bordo/10 text-bordo-dark'
                : 'border-tinta/15 bg-white text-tinta-soft hover:bg-creme-light hover:text-tinta'
            }`}
          >
            <div className="flex flex-col items-center justify-center space-y-1">
              <ExternalLink className="h-4 w-4" />
              <span className="hidden sm:inline">Link</span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => {
              onTermsTypeChange('internal');
              if (termsType !== 'internal') {
                onTermsContentChange('');
                setEditorState(EditorState.createEmpty());
              }
            }}
            className={`px-2 py-2.5 border rounded-lg transition-colors text-xs sm:text-sm font-bold ${
              termsType === 'internal'
                ? 'border-bordo bg-bordo/10 text-bordo-dark'
                : 'border-tinta/15 bg-white text-tinta-soft hover:bg-creme-light hover:text-tinta'
            }`}
          >
            <div className="flex flex-col items-center justify-center space-y-1">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Editor</span>
            </div>
          </button>
        </div>
      </div>

      {termsType === 'external' && (
        <div className="mt-3 pl-4 border-l-2 border-bordo/30 space-y-2">
          <label htmlFor="terms-url" className="block text-xs font-bold uppercase tracking-wider text-tinta">
            URL do Regulamento
          </label>
          <input
            type="url"
            id="terms-url"
            value={termsContent || ''}
            onChange={(e) => onTermsContentChange(e.target.value)}
            placeholder="https://exemplo.com/regulamento"
            className="mt-1 block w-full bg-white border border-tinta/15 rounded-lg px-3 py-2.5 text-sm text-tinta placeholder:text-tinta-soft/70 placeholder:italic focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors"
          />
          <p className="mt-1 text-xs text-tinta-soft italic">
            Cole o link externo do regulamento
          </p>
        </div>
      )}

      {termsType === 'internal' && (
        <div className="mt-3 pl-4 border-l-2 border-bordo/30 space-y-2">
          <label className="block text-xs font-bold uppercase tracking-wider text-tinta">
            Conteúdo do Regulamento
          </label>
          <div className="border border-tinta/15 rounded-lg overflow-hidden bg-white">
            <Editor
              editorState={editorState}
              onEditorStateChange={handleEditorChange}
              toolbar={{
                options: ['inline', 'list', 'link', 'history'],
                inline: {
                  options: ['bold', 'italic']
                },
                list: {
                  options: ['unordered', 'ordered']
                },
                link: {
                  options: ['link']
                }
              }}
              editorClassName="min-h-[150px] px-3 py-2 text-sm"
              placeholder="Digite o regulamento aqui..."
            />
          </div>
          <p className="mt-1 text-xs text-tinta-soft italic">
            Formate o texto usando a barra de ferramentas
          </p>
        </div>
      )}
    </div>
  );
};

export default TermsEditor;
